/**
 * ReceiveFormDialog — bám đúng Access spec `frmFactoryQryReceiveAddStore`
 * (`plan/access_all/form_frmFactoryQryReceiveAddStore.txt`).
 *
 * Visible user-editable fields (theo TabIndex Access, `strField` Form_Load):
 *    1. grn_id (required, disabled trong edit mode — là PK)
 *    2. date_in (date)
 *    3. warehouse (ComboBox from public.list_warehouse; default từ
 *       factory_overview.receive_warehouse — FE chỉ Autocomplete free-solo
 *       trên giá trị có sẵn trong existingRows)
 *    4. suppliers (Autocomplete from public.cp_vn.short_name)
 *    5. order_no (Autocomplete; RowSource đổi theo suppliers — simplified ở FE)
 *    6. truck_no
 *    7. quality_code (required, ComboBox from public.list_and_valo)
 *    8. origin
 *    9. package (ComboBox from public.package — code+label)
 *   10. kg_per_bag (number, auto-calc khi net_quantity_kg / no_of_bag đổi)
 *   11. gross_quantity (number)
 *   12. no_of_bag (number)
 *   13. net_quantity_kg (number)
 *   14. stack_id (required, ComboBox from stack table)
 *   15. area (ComboBox from public.factory_area)
 *   16. stack_or_pallet
 *   17. crop
 *   18. issue_to_processing
 *   19. note (multiline)
 *
 * Hidden system fields KHÔNG render: id, id2, is_status, created_*, updated_*.
 * Readonly DLookup labels (Package Name, Stack Area Original/Latest, Order No
 * Certification/License) — chỉ render khi BE expose; tạm bỏ để giữ form gọn.
 *
 * Validation (theo VBA `Form_Load` + `btnSave_Click`):
 *   - strCheck = ",grn_id,quality_code,stack_id,"
 *   - grn_id_AfterUpdate: duplicate check via DCount; FE pre-check trên
 *     existingRows (BE vẫn authoritative).
 *
 * Auto-calc (theo `net_quantity_kg_AfterUpdate` / `no_of_bag_AfterUpdate`):
 *   kg_per_bag = net_quantity_kg / no_of_bag
 *
 * Payload (theo `strU_mainsub`/`strI_mainsub`):
 *   Update: date_in,warehouse,order_no,suppliers,truck_no,quality_code,origin,
 *           package,kg_per_bag,gross_quantity,no_of_bag,net_quantity_kg,
 *           stack_id,area,stack_or_pallet,crop,issue_to_processing,note
 *   Insert: grn_id,(...same fields above...)
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

import type {
  FactoryCell,
  FactoryBRowPayload,
} from '@/store/api/factoryApi'
import {
  useGetReceiveLookupsQuery,
  useGetReferenceCombosQuery,
  useLazyCheckGrnIdExistsQuery,
  useLazyGetSuggestGrnIdQuery,
  useLazyGetReceiveOrderNoOptionsQuery,
} from '@/store/api/factoryApi'
import { FORM_GRID_2COL, HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '@/theme/responsive'

type ReceiveRow = { id: string | number } & Record<string, FactoryCell>

interface DraftState {
  grn_id: string
  date_in: string
  warehouse: string
  suppliers: string
  order_no: string
  truck_no: string
  quality_code: string
  origin: string
  package: string
  kg_per_bag: string
  gross_quantity: string
  no_of_bag: string
  net_quantity_kg: string
  stack_id: string
  area: string
  stack_or_pallet: string
  crop: string
  issue_to_processing: string
  note: string
}

const EMPTY_DRAFT: DraftState = {
  grn_id: '',
  date_in: '',
  warehouse: '',
  suppliers: '',
  order_no: '',
  truck_no: '',
  quality_code: '',
  origin: '',
  package: '',
  kg_per_bag: '',
  gross_quantity: '',
  no_of_bag: '',
  net_quantity_kg: '',
  stack_id: '',
  area: '',
  stack_or_pallet: '',
  crop: '',
  issue_to_processing: '',
  note: '',
}

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}

// Skill `/convert-form` readonly bg standard: neutral gray cố định thay MUI
// default disabled (trên theme TLC cream nhìn phớt hồng).
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
  },
}

/** Skill `/convert-form` Format Standard: số căn phải + inputMode decimal. */
const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

/** Parse "1,234.5" → 1234.5 (strip thousand commas); rỗng/NaN → null. */
function parseNumeric(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

/** Format raw → "1,234.56" (Access Format="Standard": thousand sep + 2 dec). */
function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** Option code+name cho ComboBox lookup (quality_code, package): hiển thị
 *  flat `"<CODE> — <NAME>"`; collapse về code khi name rỗng/trùng code. */
type CodeNameOption = { value: string; label: string }
function codeNameLabel(o: CodeNameOption | null): string {
  if (!o) return ''
  const code = String(o.value ?? '')
  const name = String(o.label ?? '')
  return !name || name === code ? code : `${code} — ${name}`
}
/** Đảm bảo giá trị đã lưu (edit/copy) luôn nằm trong options dù master đã đổi. */
function injectCurrentOption(options: CodeNameOption[], current: string): CodeNameOption[] {
  const v = String(current ?? '').trim()
  if (!v || options.some((o) => o.value === v)) return options
  return [{ value: v, label: '' }, ...options]
}

/** Distinct trimmed values của 1 field từ rows hiện có, sort vi locale. */
function distinctValues(rows: ReceiveRow[], field: string): string[] {
  const set = new Set<string>()
  for (const r of rows) {
    const v = String(r[field] ?? '').trim()
    if (v) set.add(v)
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'vi'))
}

/** Đảm bảo giá trị hiện tại (đã lưu khi edit/copy) nằm trong options dù master
 *  list không còn chứa nó (đã xóa / đổi) — tránh Autocomplete hiện blank. */
function injectCurrent(options: string[], current: string): string[] {
  const v = String(current ?? '').trim()
  if (!v || options.includes(v)) return options
  return [v, ...options]
}

/** Tính kg_per_bag = net_quantity_kg / no_of_bag (Access AfterUpdate logic).
 *  Round 4 chữ số để tránh float noise; trả '' nếu chia 0 / NaN. */
function calcKgPerBag(netKg: string, noOfBag: string): string {
  const n = parseNumeric(netKg)
  const b = parseNumeric(noOfBag)
  if (n == null || b == null || b === 0) return ''
  const v = n / b
  return Number.isFinite(v) ? formatStandard(Math.round(v * 10000) / 10000) : ''
}

interface ReceiveFormDialogProps {
  open: boolean
  /** 'new' = empty draft + new grn_id; 'edit' = hydrate row, grn_id locked;
   *  'copy' = hydrate row (clone fields) nhưng reset grn_id để user nhập mã mới
   *  (bám Access `btnCopy_Click`: clone RecordSource + Suggest_IndexName mới
   *  + id=0 → Save sẽ INSERT). */
  mode: 'new' | 'edit' | 'copy'
  /** Row hiện tại (khi edit/copy). null khi new. */
  row: ReceiveRow | null
  saving: boolean
  /** Toàn bộ rows hiện có — dùng cho Autocomplete options + duplicate check. */
  existingRows: ReceiveRow[]
  /** Factory code — gọi suggest GRN id endpoint khi new/copy. */
  factoryCode: string
  onClose: () => void
  onSave: (payload: FactoryBRowPayload) => void
}

export function ReceiveFormDialog({
  open,
  mode,
  row,
  saving,
  existingRows,
  factoryCode,
  onClose,
  onSave,
}: ReceiveFormDialogProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [triggerSuggest, suggestState] = useLazyGetSuggestGrnIdQuery()
  const [triggerCheckGrn] = useLazyCheckGrnIdExistsQuery()
  /** Lock quality_code/origin/package khi stack_id đã được dùng bởi receive
   *  khác (Access `stack_id_AfterUpdate` Case B). Reset mỗi lần mở dialog;
   *  chỉ chuyển true sau khi user actively chọn stack_id (mimic AfterUpdate
   *  — không lock ngay lúc hydrate Edit/Copy). */
  const [stackLocked, setStackLocked] = useState(false)
  /** Cảnh báo inline khi pre-save check phát hiện GRN ID đã có row khác
   *  dùng → tự suggest mã mới + alert user xác nhận Save lần 2 (KHÔNG
   *  auto-save). */
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  /** Loading flag riêng cho pre-save check để disable nút Save khi đang
   *  gọi BE. */
  const [precheckBusy, setPrecheckBusy] = useState(false)
  /** Synchronous mirror của precheckBusy — React state setState async, nếu
   *  user click Save NGAY trong lúc blur field grn_id (focus → button),
   *  browser fire blur + click cùng tick → state setState chưa flush →
   *  handleSave vẫn thấy precheckBusy=false → POST fire với grn_id chưa
   *  verify. Ref cho phép check sync. */
  const precheckBusyRef = useRef(false)
  /** Đánh dấu user vừa commit stack_id → effect set `area` = stack_area_latest
   *  khi lookup về (Access stack_id_AfterUpdate: `Me.area = stack_area_new`).
   *  Không set lúc hydrate/edit (chỉ khi user actively chọn stack). */
  const stackPickedRef = useRef(false)

  /** Hydrate draft khi dialog mở:
   *  - edit → row values, grn_id giữ nguyên.
   *  - copy → row values nhưng grn_id reset '' để user nhập mã mới.
   *  - new → empty defaults. */
  useEffect(() => {
    if (!open) return
    if ((mode === 'edit' || mode === 'copy') && row) {
      setDraft({
        grn_id: mode === 'copy' ? '' : String(row.grn_id ?? ''),
        date_in: String(row.date_in ?? '').slice(0, 10),
        warehouse: String(row.warehouse ?? ''),
        suppliers: String(row.suppliers ?? ''),
        order_no: String(row.order_no ?? ''),
        truck_no: String(row.truck_no ?? ''),
        quality_code: String(row.quality_code ?? ''),
        origin: String(row.origin ?? ''),
        package: String(row.package ?? row.packaging ?? ''),
        kg_per_bag: formatStandard(row.kg_per_bag as number | string | null),
        gross_quantity: formatStandard(row.gross_quantity as number | string | null),
        no_of_bag: formatStandard(row.no_of_bag as number | string | null),
        net_quantity_kg: formatStandard(row.net_quantity_kg as number | string | null),
        stack_id: String(row.stack_id ?? ''),
        area: String(row.area ?? ''),
        stack_or_pallet: String(row.stack_or_pallet ?? ''),
        crop: String(row.crop ?? ''),
        issue_to_processing: formatStandard(row.issue_to_processing as number | string | null),
        note: String(row.note ?? ''),
      })
    } else {
      setDraft({
        ...EMPTY_DRAFT,
        // Default warehouse set bởi effect dưới (Access DefaultValue =
        // DLookup receive_warehouse theo factory).
        date_in: new Date().toISOString().slice(0, 10),
      })
    }
    // CHÚ Ý: deps không include `existingRows` — list rows có thể refetch
    // (vd: form khác save → invalidate cache) gây re-render. Nếu phụ thuộc
    // existingRows, effect sẽ reset draft và mất mọi giá trị user đang
    // nhập (kể cả grn_id đã được suggest mã mới). Chỉ re-hydrate khi
    // open/mode/row thật sự đổi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, row])

  /** Auto-suggest grn_id cho new/copy — bám Access `btnNew_Click` /
   *  `btnCopy_Click`: `grn_id = Suggest_IndexName(... & "receive")`. Apply 1
   *  lần khi suggestion arrive và field còn rỗng (user có thể đã type tay
   *  trước khi BE response → không overwrite). */
  useEffect(() => {
    if (!open) return
    if (mode === 'edit') return
    void triggerSuggest({ factoryCode })
  }, [open, mode, factoryCode, triggerSuggest])

  useEffect(() => {
    const suggested = suggestState.data?.grn_id
    if (!suggested) return
    if (mode === 'edit') return
    setDraft((prev) => (prev.grn_id.trim().length ? prev : { ...prev, grn_id: suggested }))
  }, [suggestState.data, mode])

  /** Reset stack lock mỗi khi dialog mở lại (Access — chỉ kick sau khi
   *  AfterUpdate fire, không phải lúc Form_Load). */
  useEffect(() => {
    if (!open) return
    setStackLocked(false)
    setRegenNotice(null)
    setPrecheckBusy(false)
  }, [open, mode, row])

  /** Khi user sửa grn_id (sau khi đã bị regen) — clear notice cũ. */
  useEffect(() => {
    if (regenNotice && draft.grn_id !== regenNotice.to) {
      setRegenNotice(null)
    }
  }, [draft.grn_id, regenNotice])

  /** Master-data combos đọc thẳng bảng master (Access RowSource), thay cho
   *  distinctValues derive từ data trên trang. */
  const { data: combos } = useGetReferenceCombosQuery({ factoryCode }, { skip: !open })

  /** Warehouse KHÓA = receive_warehouse của factory (Access DefaultValue).
   *  Set khi new/copy (record mới). Edit giữ warehouse của row. */
  useEffect(() => {
    if (!open || mode === 'edit') return
    const w = combos?.receiveWarehouse
    if (w) setDraft((d) => (d.warehouse === w ? d : { ...d, warehouse: w }))
  }, [open, mode, combos?.receiveWarehouse])

  /** Options từ master combos. `injectCurrent` đảm bảo giá trị đã lưu
   *  (edit/copy) luôn nằm trong list dù master đã đổi. */
  // suppliers combo (LimitToList) phải có 2 keyword đặc biệt "PROCESSING" /
  // "INTERNAL MOVING" để switch order_no sang PO / moving — 2 giá trị này KHÔNG
  // nằm trong cp_vn nên prepend thủ công. BE compare lower-case nên viết hoa
  // vẫn match nhánh PO/moving.
  const suppliersOptions = useMemo(() => {
    const base = combos?.suppliers?.map((o) => o.short_name) ?? []
    return injectCurrent(['PROCESSING', 'INTERNAL MOVING', ...base], draft.suppliers)
  }, [combos, draft.suppliers])
  /** Access `suppliers_AfterUpdate` (frmFactoryQryReceiveAddStore:3505): order_no
   *  RowSource switch theo suppliers (processing→PO / internal moving→moving /
   *  else→purchase contract). Mỗi option kèm Quality Code để show thêm. */
  const [orderNoOptions, setOrderNoOptions] = useState<
    Array<{
      order_no: string
      quality: string | null
      processing_type: string | null
      supplier: string | null
    }>
  >([])
  const [fetchOrderNoOptions] = useLazyGetReceiveOrderNoOptionsQuery()
  useEffect(() => {
    if (!open) return
    // Access Form_Load đặt order_no.RowSource = QryAllPurchaseContract NGAY khi
    // mở (suppliers rỗng → BE nhánh else trả purchase contract);
    // suppliers_AfterUpdate mới switch sang PO/moving. → luôn fetch.
    const s = draft.suppliers.trim()
    void fetchOrderNoOptions({ factoryCode, suppliers: s })
      .unwrap()
      .then((rows) => setOrderNoOptions(Array.isArray(rows) ? rows : []))
      .catch(() => setOrderNoOptions([]))
  }, [open, draft.suppliers, factoryCode, fetchOrderNoOptions])
  // quality_code: Access Receive ORDER BY created_date desc = thứ tự server trả.
  // Option code+name flat `"<code> — <description>"` (skill §ComboBox).
  const qualityOptions = useMemo(
    () =>
      injectCurrentOption(
        combos?.qualityCodes?.map((o) => ({ value: o.code, label: o.description ?? '' })) ?? [],
        draft.quality_code,
      ),
    [combos, draft.quality_code],
  )
  // package: flat `"<packaging_code> — <packaging>"`.
  const packageOptions = useMemo(
    () =>
      injectCurrentOption(
        combos?.packages?.map((o) => ({ value: o.packaging_code, label: o.packaging ?? '' })) ?? [],
        draft.package,
      ),
    [combos, draft.package],
  )
  // Stack ID option kèm cờ from_po ("— From PO") + quality ("— <quality>").
  const stackIdOptions = useMemo<
    Array<{ value: string; from_po: boolean | null; quality: string | null }>
  >(() => {
    const base =
      combos?.stackIds?.map((o) => ({
        value: o.stack_id,
        from_po: o.is_received_from_po,
        quality: o.quality,
      })) ?? []
    const v = draft.stack_id.trim()
    if (v && !base.some((o) => o.value === v))
      return [{ value: v, from_po: null, quality: null }, ...base]
    return base
  }, [combos, draft.stack_id])
  const areaOptions = useMemo(
    () => injectCurrent(combos?.areas?.map((o) => o.full_area_code) ?? [], draft.area),
    [combos, draft.area],
  )
  // origin / stack_or_pallet / crop là TextBox trong Access (không phải combo
  // bound master) → giữ free-text gợi ý từ rows hiện có.
  const originOptions = useMemo(() => distinctValues(existingRows, 'origin'), [existingRows])
  const stackOrPalletOptions = useMemo(() => distinctValues(existingRows, 'stack_or_pallet'), [existingRows])
  const cropOptions = useMemo(() => distinctValues(existingRows, 'crop'), [existingRows])

  /** Duplicate check trên grn_id (parity VBA: DCount on tlc_factory_bl_receive).
   *  Active cho cả 'new' và 'copy' (cả 2 đều insert mã mới). Chỉ pre-check ở
   *  FE — BE vẫn authoritative. */
  const duplicateGrnId = useMemo(() => {
    if (mode === 'edit') return false
    const id = draft.grn_id.trim()
    if (!id) return false
    return existingRows.some((r) => String(r.grn_id ?? '').trim() === id)
  }, [draft.grn_id, existingRows, mode])

  /** Parity Access `Form_Error` (3162 "can not be empty"): đánh dấu field đã
   *  rời ô (touched) → bôi đỏ + helper "Cannot be empty" nếu required còn rỗng.
   *  Reset khi mở lại dialog. */
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  useEffect(() => {
    if (open) setTouched({})
  }, [open])
  const markTouched = (k: string) => setTouched((t) => (t[k] ? t : { ...t, [k]: true }))

  const grnIdEmpty = draft.grn_id.trim().length === 0
  const qualityEmpty = draft.quality_code.trim().length === 0
  const stackIdEmpty = draft.stack_id.trim().length === 0
  const canSave =
    !grnIdEmpty &&
    !qualityEmpty &&
    !stackIdEmpty &&
    !duplicateGrnId &&
    !saving

  const buildPayload = (): FactoryBRowPayload => {
    /** Payload — Access spec line 3360/3361 (strU_mainsub / strI_mainsub).
     *  String fields: trim → null nếu rỗng. Number fields: parseFloat → null
     *  nếu NaN. BE schema chấp nhận FactoryCell (string|number|null). */
    const str = (v: string): string | null => {
      const s = v.trim()
      return s.length ? s : null
    }
    const num = (v: string): number | null => parseNumeric(v)
    return {
      grn_id: draft.grn_id.trim(), // luôn có vì required
      date_in: str(draft.date_in),
      warehouse: str(draft.warehouse),
      order_no: str(draft.order_no),
      suppliers: str(draft.suppliers),
      truck_no: str(draft.truck_no),
      quality_code: draft.quality_code.trim(), // required
      origin: str(draft.origin),
      package: str(draft.package),
      kg_per_bag: num(draft.kg_per_bag),
      gross_quantity: num(draft.gross_quantity),
      no_of_bag: num(draft.no_of_bag),
      net_quantity_kg: num(draft.net_quantity_kg),
      stack_id: draft.stack_id.trim(), // required
      area: str(draft.area),
      stack_or_pallet: str(draft.stack_or_pallet),
      crop: str(draft.crop),
      issue_to_processing: num(draft.issue_to_processing),
      note: str(draft.note),
    }
  }

  const handleSave = () => {
    // Race guard: nếu blur field grn_id và click Save xảy ra cùng tick
    // (user click thẳng nút Save khi field đang focus), blur sẽ fire
    // handleGrnIdBlur (async) trước, set ref true ngay. Click Save tới
    // đây phải bỏ qua, đợi precheck resolve mới Save tiếp.
    if (precheckBusyRef.current) return
    if (!canSave) return
    onSave(buildPayload())
  }

  /** Access `grn_id_AfterUpdate` parity — fire khi user rời field grn_id
   *  (Tab/click ra ngoài). Nếu mã đã tồn tại (active row khác) → call BE
   *  suggest mã mới → cập nhật vào field + show inline Alert thông báo.
   *  Edit không check (PK không đổi). */
  const handleGrnIdBlur = async () => {
    if (mode === 'edit') return
    const grn = draft.grn_id.trim()
    if (!grn) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheckGrn({ factoryCode, grnId: grn }).unwrap()
      if (!check.exists) return
      // preferCacheValue=false → force network refetch. Cùng hook dùng cho
      // lần suggest đầu khi mở form đã subscribe cache; nếu không force,
      // lazy trigger trả mã cũ = mã đang trùng → newGrn === grn → skip.
      const next = await triggerSuggest({ factoryCode }, false).unwrap()
      const newGrn = (next.grn_id || '').trim()
      if (newGrn && newGrn !== grn) {
        setDraft((prev) => ({ ...prev, grn_id: newGrn }))
        setRegenNotice({ from: grn, to: newGrn })
      }
    } catch {
      // Network/permission lỗi — bỏ qua, BE sẽ raise lúc Save nếu thật sự trùng.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  const handleChange = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  /** Access `stack_id_AfterUpdate` — fire khi user commit stack_id qua combo.
   *  Case A: stack mới → enable + clear quality/origin/package, giữ area =
   *          stack_area_latest.
   *  Case B: stack đã được dùng bởi receive khác → disable quality/origin/
   *          package + auto-fill từ view_factory_stack1_v2 (FE proxy: lấy
   *          từ existingRows có cùng stack_id).
   *  Case C: stack_id null → enable + clear cả 4 field.
   *  Mode Edit excludes current row khỏi lookup (không tự count chính mình). */
  const handleStackIdCommit = (next: string) => {
    const trimmed = next.trim()
    if (!trimmed) {
      // stack_id null → enable + clear cả 4 field (area cũng clear).
      stackPickedRef.current = false
      setStackLocked(false)
      setDraft((prev) => ({
        ...prev,
        stack_id: next,
        quality_code: '',
        area: '',
        origin: '',
        package: '',
      }))
      return
    }
    // User chọn stack → đánh dấu để effect set `area` = stack_area_latest
    // (Access set `Me.area = stack_area_new` ở mọi nhánh stack non-null).
    stackPickedRef.current = true
    const currentId = String(row?.id ?? '')
    const ref = existingRows.find((r) => {
      if (String(r.stack_id ?? '').trim() !== trimmed) return false
      if (Number(r.is_status ?? 0) !== 0) return false
      // Edit mode: bỏ qua row đang sửa khi count receive khác.
      if (mode === 'edit' && currentId && String(r.id) === currentId) return false
      return true
    })
    if (ref) {
      // Case B: stack đã dùng → lock + autofill quality/origin/package.
      setStackLocked(true)
      setDraft((prev) => ({
        ...prev,
        stack_id: next,
        quality_code: String(ref.quality_code ?? ''),
        origin: String(ref.origin ?? ''),
        package: String(ref.package ?? ref.packaging ?? ''),
      }))
      return
    }
    // Case C: stack mới → enable + clear quality/origin/package.
    setStackLocked(false)
    setDraft((prev) => ({
      ...prev,
      stack_id: next,
      quality_code: '',
      origin: '',
      package: '',
    }))
  }

  /** Authoritative DLookup từ BE — query mỗi khi stack_id/order_no/package
   *  trong draft change (RTK keepUnusedDataFor=30s cache theo args).
   *  Skip request khi cả 3 input rỗng để tránh hit BE thừa.
   *  Returns: stack_area_original/latest, certification, license_cert_no,
   *  packaging (Package Name). */
  const lookupArgs = {
    factoryCode,
    stackId: draft.stack_id.trim() || undefined,
    orderNo: draft.order_no.trim() || undefined,
    package: draft.package.trim() || undefined,
  }
  const skipLookup = !lookupArgs.stackId && !lookupArgs.orderNo && !lookupArgs.package
  const { data: lookups, isFetching: lookupsFetching } = useGetReceiveLookupsQuery(lookupArgs, {
    skip: skipLookup,
  })

  /** Access stack_id_AfterUpdate: `Me.area = stack_area_new` (view_factory_stack4).
   *  BE trả `stack_area_latest` = stack_area_new. Set khi user vừa chọn stack
   *  (stackPickedRef) và lookup đã về (không còn fetching → tránh dính giá trị
   *  stack cũ trong lúc refetch). Set xong clear cờ để không đè area user sửa. */
  useEffect(() => {
    if (!stackPickedRef.current || lookupsFetching) return
    const latest = lookups?.stack_area_latest ?? ''
    setDraft((prev) => (prev.area === latest ? prev : { ...prev, area: latest }))
    stackPickedRef.current = false
  }, [lookups?.stack_area_latest, lookupsFetching])

  const stackOriginalDisplay = lookups?.stack_area_original ?? ''
  const stackLatestDisplay = lookups?.stack_area_latest ?? ''
  const orderCertDisplay = lookups?.certification ?? ''
  const orderLicenseDisplay = lookups?.license_cert_no ?? ''
  const packageNameDisplay = lookups?.packaging ?? ''

  /** Recalc kg_per_bag mỗi khi net_quantity_kg hoặc no_of_bag đổi (parity
   *  AfterUpdate handlers). User vẫn có thể manual override sau khi auto-calc.
   */
  const handleNetOrBagChange = (key: 'net_quantity_kg' | 'no_of_bag', value: string) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value }
      next.kg_per_bag = calcKgPerBag(next.net_quantity_kg, next.no_of_bag)
      return next
    })
  }

  /** Auto-format số sang Standard "1,234.56" khi rời ô (skill: typing raw,
   *  blur normalize). kg_per_bag re-calc nên không cần blur format riêng. */
  const handleNumericBlur = (key: keyof DraftState) => () => {
    setDraft((prev) => {
      const formatted = formatStandard(prev[key])
      return formatted === prev[key] ? prev : { ...prev, [key]: formatted }
    })
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        onClose()
      }}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(820px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          component="span"
          noWrap
          title={`${mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'} — Factory · Receive`}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'} —
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            {' Factory ·'}
          </Box>
          {' Receive'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canSave}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          ...compactInputSx,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          pt: 1.5,
        }}
      >
        {duplicateGrnId ? (
          <Alert severity="error" sx={{ mb: 1.25, py: 0.25 }}>
            This GRN ID already exists — please use a different code.
          </Alert>
        ) : null}
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            GRN ID <b>{regenNotice.from}</b> was just created — suggested new code{' '}
            <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}

        {/* 2-col grid — bám đúng layout Access `frmFactoryQryReceiveAddStore`
         *  (Left=3180 cột trái, Left=11496 cột phải, 13 hàng theo Top từ 60
         *  đến 4608, không tự sáng tạo). 5 readonly DLookup fields render
         *  disabled placeholder để giữ đúng vị trí spec. */}
        <Box sx={FORM_GRID_2COL}>
          {/* Row Top=60 — grn_id * | date_in */}
          <TextField
            label="GRN ID"
            size="small"
            fullWidth
            required
            autoFocus={mode === 'new' || mode === 'copy'}
            value={draft.grn_id}
            onChange={(e) => handleChange('grn_id', e.target.value)}
            onBlur={() => {
              markTouched('grn_id')
              void handleGrnIdBlur()
            }}
            disabled={mode === 'edit'}
            error={(touched.grn_id && grnIdEmpty) || (!grnIdEmpty && duplicateGrnId)}
            helperText={
              mode === 'edit'
                ? 'GRN ID cannot be changed after creation.'
                : precheckBusy
                  ? 'Verifying GRN code…'
                  : suggestState.isFetching
                    ? 'Suggesting next GRN code…'
                    : mode === 'copy'
                      ? `Suggested code below; cloned from ${String(row?.grn_id ?? '')}.`
                      : 'Suggested code below; edit if needed.'
            }
          />
          <DatePicker
            label="Date In"
            format="DD-MMM-YYYY"
            value={draft.date_in ? dayjs(draft.date_in) : null}
            onChange={(d) =>
              handleChange('date_in', d && d.isValid() ? d.format('YYYY-MM-DD') : '')
            }
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                InputLabelProps: { shrink: true },
              },
            }}
          />

          {/* Row Top=444 — warehouse | (empty).
           *  Access `Me.warehouse.DefaultValue = DLookup(receive_warehouse,…)`:
           *  mỗi nhà máy đúng 1 warehouse, KHÓA không cho sửa. */}
          <TextField
            label="Warehouse"
            size="small"
            fullWidth
            disabled
            value={draft.warehouse}
            sx={readonlyFieldSx}
          />
          <Box />

          {/* Row Top=804 — stack_id * | suppliers.
           *  onChange (commit selection) → fire stack_id_AfterUpdate; onInputChange
           *  chỉ update text để giữ free-solo typing. */}
          <Autocomplete<
            { value: string; from_po: boolean | null; quality: string | null },
            false,
            false,
            false
          >
            size="small"
            options={stackIdOptions}
            getOptionLabel={(o) => {
              const parts = [o.value]
              if (o.quality) parts.push(o.quality)
              if (o.from_po) parts.push('From PO')
              return parts.join(' — ')
            }}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            value={stackIdOptions.find((o) => o.value === draft.stack_id) ?? null}
            onChange={(_, v) => handleStackIdCommit(v?.value ?? '')}
            onBlur={() => markTouched('stack_id')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Stack ID"
                required
                fullWidth
                error={touched.stack_id && stackIdEmpty}
              />
            )}
          />
          <Autocomplete<string, false, false, false>
            size="small"
            options={suppliersOptions}
            value={draft.suppliers || null}
            onChange={(_, v) =>
              // Đổi Supplier → order_no RowSource switch (PO/moving/contract) →
              // clear order_no để user chọn lại từ nguồn mới.
              setDraft((prev) => ({ ...prev, suppliers: String(v ?? ''), order_no: '' }))
            }
            renderInput={(params) => <TextField {...params} label="Suppliers" fullWidth />}
          />

          {/* Row Top=1188 — stack_area_original (RO) | stack_area_latest (RO).
           *  BE lookup view_factory_stack4 theo stack_id (Access `stack_id_AfterUpdate`). */}
          <TextField
            label="Stack Area (Original)"
            size="small"
            fullWidth
            disabled
            value={stackOriginalDisplay}
            sx={readonlyFieldSx}
          />
          <TextField
            label="Stack Area (Latest)"
            size="small"
            fullWidth
            disabled
            value={stackLatestDisplay}
            sx={readonlyFieldSx}
          />

          {/* Row Top=1572 — truck_no | order_no */}
          <TextField
            label="Truck No"
            size="small"
            fullWidth
            value={draft.truck_no}
            onChange={(e) => handleChange('truck_no', e.target.value)}
          />
          <Autocomplete<
            {
              order_no: string
              quality: string | null
              processing_type: string | null
              supplier: string | null
            },
            false,
            false,
            false
          >
            size="small"
            options={orderNoOptions}
            getOptionLabel={(o) => {
              // PO No → kèm processing type; contract → kèm supplier name; luôn
              // kèm quality nếu có. Bỏ phần rỗng, nối " — ".
              const parts = [o.order_no]
              if (o.processing_type) parts.push(o.processing_type)
              if (o.supplier) parts.push(o.supplier)
              if (o.quality) parts.push(o.quality)
              return parts.join(' — ')
            }}
            isOptionEqualToValue={(a, b) => a.order_no === b.order_no}
            value={
              orderNoOptions.find((o) => o.order_no === draft.order_no) ??
              (draft.order_no
                ? { order_no: draft.order_no, quality: null, processing_type: null, supplier: null }
                : null)
            }
            onChange={(_, v) => handleChange('order_no', v?.order_no ?? '')}
            renderInput={(params) => <TextField {...params} label="Order No" fullWidth />}
          />

          {/* Row Top=1932 — Order No - Certification (RO) | Order No - License (RO).
           *  DLookup từ QryAllPurchaseContract theo contract_no=[order_no];
           *  FE đọc thẳng từ row (BE alias `certification`/`license_cert_no`). */}
          <TextField
            label="Order No - Certification"
            size="small"
            fullWidth
            disabled
            value={orderCertDisplay}
            sx={readonlyFieldSx}
          />
          <TextField
            label="Order No - License"
            size="small"
            fullWidth
            disabled
            value={orderLicenseDisplay}
            sx={readonlyFieldSx}
          />

          {/* Row Top=2316 — quality_code * | origin.
           *  3 field này (quality/origin/package) lock theo Access Case B
           *  trong stack_id_AfterUpdate (stack đã được dùng → values phải
           *  giữ đúng theo stack, không cho sửa). */}
          <Autocomplete<CodeNameOption, false, false, false>
            disabled={stackLocked}
            size="small"
            options={qualityOptions}
            getOptionLabel={codeNameLabel}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            value={qualityOptions.find((o) => o.value === draft.quality_code) ?? null}
            onChange={(_, v) => handleChange('quality_code', v?.value ?? '')}
            onBlur={() => markTouched('quality_code')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Quality Code"
                required
                fullWidth
                error={touched.quality_code && qualityEmpty && !stackLocked}
                helperText={stackLocked ? 'Locked by selected Stack ID.' : undefined}
              />
            )}
          />
          <Autocomplete<string, false, false, true>
            freeSolo
            disabled={stackLocked}
            size="small"
            options={originOptions}
            value={draft.origin}
            onChange={(_, v) => handleChange('origin', String(v ?? ''))}
            onInputChange={(_, v) => handleChange('origin', v)}
            renderInput={(params) => <TextField {...params} label="Origin" fullWidth />}
          />

          {/* Row Top=2700 — package | kg_per_bag. package cũng lock theo Case B. */}
          <Autocomplete<CodeNameOption, false, false, false>
            disabled={stackLocked}
            size="small"
            options={packageOptions}
            getOptionLabel={codeNameLabel}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            value={packageOptions.find((o) => o.value === draft.package) ?? null}
            onChange={(_, v) => handleChange('package', v?.value ?? '')}
            renderInput={(params) => <TextField {...params} label="Package" fullWidth />}
          />
          <TextField
            label="Kg Per Bag"
            size="small"
            fullWidth
            type="text"
            value={draft.kg_per_bag}
            onChange={(e) => handleChange('kg_per_bag', e.target.value)}
            onBlur={handleNumericBlur('kg_per_bag')}
            inputProps={numericInputProps}
            helperText="Auto-calculated from Net Quantity Kg ÷ No Of Bag (editable)."
          />

          {/* Row Top=3084 — Package Name (RO DLookup) | no_of_bag.
           *  DLookup `packaging` từ public.package theo packaging_code; FE
           *  đọc `row.packaging` (BE expose label) hoặc fallback `row.package`. */}
          <TextField
            label="Package Name"
            size="small"
            fullWidth
            disabled
            value={packageNameDisplay}
            sx={readonlyFieldSx}
          />
          <TextField
            label="No Of Bag"
            size="small"
            fullWidth
            type="text"
            value={draft.no_of_bag}
            onChange={(e) => handleNetOrBagChange('no_of_bag', e.target.value)}
            onBlur={handleNumericBlur('no_of_bag')}
            inputProps={numericInputProps}
          />

          {/* Row Top=3468 — gross_quantity | area */}
          <TextField
            label="Gross Quantity"
            size="small"
            fullWidth
            type="text"
            value={draft.gross_quantity}
            onChange={(e) => handleChange('gross_quantity', e.target.value)}
            onBlur={handleNumericBlur('gross_quantity')}
            inputProps={numericInputProps}
          />
          <Autocomplete<string, false, false, false>
            size="small"
            options={areaOptions}
            value={draft.area || null}
            onChange={(_, v) => handleChange('area', String(v ?? ''))}
            renderInput={(params) => <TextField {...params} label="Area" fullWidth />}
          />

          {/* Row Top=3852 — net_quantity_kg | crop */}
          <TextField
            label="Net Quantity Kg"
            size="small"
            fullWidth
            type="text"
            value={draft.net_quantity_kg}
            onChange={(e) => handleNetOrBagChange('net_quantity_kg', e.target.value)}
            onBlur={handleNumericBlur('net_quantity_kg')}
            inputProps={numericInputProps}
          />
          <Autocomplete<string, false, false, true>
            freeSolo
            size="small"
            options={cropOptions}
            value={draft.crop}
            onChange={(_, v) => handleChange('crop', String(v ?? ''))}
            onInputChange={(_, v) => handleChange('crop', v)}
            renderInput={(params) => <TextField {...params} label="Crop" fullWidth />}
          />

          {/* Row Top=4224/4608 — stack_or_pallet | issue_to_processing.
           *  Access để issue_to_processing 1 hàng riêng (Top=4608) nhưng note
           *  multiline đẩy nó xuống xa → ghép cạnh stack_or_pallet cho gần, note
           *  span full-width bên dưới (skill §Bước 4 merge ô trống, giữ thứ tự). */}
          <Autocomplete<string, false, false, true>
            freeSolo
            size="small"
            options={stackOrPalletOptions}
            value={draft.stack_or_pallet}
            onChange={(_, v) => handleChange('stack_or_pallet', String(v ?? ''))}
            onInputChange={(_, v) => handleChange('stack_or_pallet', v)}
            renderInput={(params) => <TextField {...params} label="Stack Or Pallet" fullWidth />}
          />
          <TextField
            label="Issue To Processing"
            size="small"
            fullWidth
            type="text"
            value={draft.issue_to_processing}
            onChange={(e) => handleChange('issue_to_processing', e.target.value)}
            onBlur={handleNumericBlur('issue_to_processing')}
            inputProps={numericInputProps}
          />

          {/* Note — span full-width cuối form (multiline). */}
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            rows={3}
            value={draft.note}
            onChange={(e) => handleChange('note', e.target.value)}
            sx={{ gridColumn: { sm: '1 / -1' } }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
